<template>
  <canvas ref="canvasElement"
          :style="canvasStyle"
          :width="canvasSize.x"
          :height="canvasSize.y" />
</template>

<script>
import getNested from 'lodash-es/get';
import L from 'leaflet';
import { apiCoordsToLatLng, leafletBoundsToApi } from '@/shared/coordinate-utils';
import { mapActions, mapMutations } from 'vuex';
import isEmpty from 'lodash-es/isEmpty';


const iconsCache = getIconsCacheObject({debug: false});


export default {
  name: 'vessels-tile-component',

  props: {
    coords: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      context: null,
      tmpContext: null,
      canvasSize: {
        x: 256,
        y: 256
      },

      // flags and constants
      canvasIsClear: true,
      maxVesselsPerTile: 50000,
    };
  },

  async mounted() {
    this.context = this.$refs.canvasElement.getContext('2d');
    this.tmpContext = this.createContext(iconsCache.iconSize.x, iconsCache.iconSize.x);
    await this.loadVesselIcons();

    if(!this.zoomInProgress && !isEmpty(this.vesselsFilterParameters)) {
      await this.drawVessels();
    }
  },

  methods: {
    createContext,
    ...mapMutations('map', [
      'clearVessels',
    ]),
    ...mapActions('map', [
      'retrieveVessels',
      'addVesselMarker',
      'removeAllMarkers',
    ]),

    async loadVesselIcons() {
      for(let imageName of Object.keys(iconsCache.icons)) {
        if(!iconsCache.icons[imageName].icon) {
          iconsCache.icons[imageName].icon = await getIconImage(
            require(`@/assets/icons/vessels/${imageName}.svg`)
          );
        }
      }
    },

    async drawVessels() {
      if(!this.canvasIsClear) {
        this.clearCanvas(this.context);
      }

      let coordinates, canvasPoint, vessels;
      if(this.cachedVesselsLoaded) {
        vessels = this.getCachedTileVessels().values();  // 10-30 ms per tile
      } else  {
        vessels = await this.getTileVessels();
      }

      for(let vessel of vessels) {
        coordinates = apiCoordsToLatLng(getNested(vessel, 'geometry.coordinates', [0, 0]));
        canvasPoint = this.latLngToCanvasCoords(coordinates);
        if(canvasPoint) {
          await this.drawVesselIcon(vessel);
          this.addVesselMarker(vessel);
        }

        if(this.zoomInProgress) {
          break;  // zooming detected, stop drawing vessels
        }
      }

      // it's needed to clear canvas next time (when new vessels props received)
      this.canvasIsClear = false;
    },

    async getTileVessels() {
      const apiBounds = leafletBoundsToApi(this.tileLatLngBounds);
      return await this.retrieveVessels({
        boundingBox: apiBounds,
        limit: this.maxVesselsPerTile,
      });
    },

    getCachedTileVessels() {
      let cachedTileVessels = new Map();
      for(let vessel of this.cachedVessels.values()) {
        const coordinates = apiCoordsToLatLng(getNested(vessel, 'geometry.coordinates', [0, 0]));
        if(this.tileLatLngBounds.contains(coordinates)) {
          cachedTileVessels.set(vessel.imo, vessel);
        }
      }
      return cachedTileVessels;
    },

    async drawVesselIcon(vessel) {
      const coordinates = apiCoordsToLatLng(getNested(vessel, 'geometry.coordinates', [0, 0]));
      const canvasPoint = this.latLngToCanvasCoords(coordinates);

      if(canvasPoint) {
        const imageCacheCoordinates = await this.getVesselIconCacheCoordinates(vessel);

        // if a point is outside of canvase, skip it
        this.context.drawImage(
          iconsCache.context.canvas,

          // source canvas coordinates
          imageCacheCoordinates.sx,
          imageCacheCoordinates.sy,
          imageCacheCoordinates.sWidth,
          imageCacheCoordinates.sHeight,

          // destination canvas coordinates
          canvasPoint.x - Math.round(imageCacheCoordinates.dWidth / 2),
          canvasPoint.y - Math.round(imageCacheCoordinates.dWidth / 2),
          imageCacheCoordinates.dWidth,  // circle: 12x12, arrow: 26x26
          imageCacheCoordinates.dHeight,
        );
      }
    },

    async getVesselIconCacheCoordinates(vessel) {
      const iconParameters = this.getVesselIconParameters(vessel);
      const cacheKey = this.getImageCacheKey(iconParameters);

      if(iconsCache.items.has(cacheKey)) {
        // retrieves coordinates from canvas cache
        // rotated image was generated already
        return iconsCache.items.get(cacheKey);
      }

      const sourceIconData = iconsCache.icons[`${iconParameters.iconType}-${iconParameters.color}`];

      this.drawIconOnTmpCanvas(sourceIconData.icon, iconParameters.roundedCourse);
      this.saveImageToCache(cacheKey, sourceIconData, this.tmpContext);
      return iconsCache.items.get(cacheKey);
    },

    getImageCacheKey(iconParameters) {
      return `${iconParameters.iconType}-${iconParameters.color}-${iconParameters.roundedCourse}`;
    },

    getVesselIconParameters(vessel) {
      let color = 'error';
      if (vessel.state && vessel.state.ladenStatus) {
        color = vessel.state.ladenStatus === 'Laden' ? 'dark' : 'light';
      }

      if (this.highlightedVesselIMO === vessel.imo) {
        color = 'highlight';
      }

      let iconType = 'circle'; // we don't know speed this case, let's set as a circle
      if (vessel.position && typeof vessel.position.speed === 'number') {
        iconType = vessel.position.speed < 1 ? 'circle' : 'arrow';
      }

      return {
        iconType,
        color,
        roundedCourse: iconType === 'circle' ? 0 : this.getVesselRoundedCourse(vessel)
      };
    },

    getVesselRoundedCourse(vessel) {
      const course = getNested(vessel, 'course', 0);
      return Math.round(course/10) * 10;
    },

    drawIconOnTmpCanvas(iconImage, angle) {
      const dWidth = iconsCache.iconSize.x;
      const dHeight = iconsCache.iconSize.y;

      this.clearCanvas(this.tmpContext);
      // this.tmpContext.strokeStyle = '#fff599';
      // this.tmpContext.strokeRect(0, 0, this.tmpContext.canvas.width, this.tmpContext.canvas.height);
      this.tmpContext.save();
      this.tmpContext.translate(Math.round(dWidth/2), Math.round(dHeight/2));
      this.tmpContext.rotate(angle * Math.PI / 180);
      this.tmpContext.translate(-Math.round(dWidth/2), -Math.round(dHeight/2));
      this.tmpContext.drawImage(iconImage, 0, 0, 100, 100, 0, 0, dWidth, dHeight);
      this.tmpContext.restore();
    },

    saveImageToCache(cacheKey, sourceIconData, imageContext) {
      const count = iconsCache.items.size;
      const colsCount = iconsCache.cols;
      const x = (count % colsCount) * iconsCache.iconSize.x;
      const y = Math.floor(count/colsCount) * iconsCache.iconSize.y;

      iconsCache.context.drawImage(
        imageContext.canvas,
        0, 0, iconsCache.iconSize.x, iconsCache.iconSize.y,
        x, y, iconsCache.iconSize.x, iconsCache.iconSize.y,
      );

      const iconCacheCoordinates = {
        sx: x,
        sy: y,
        sWidth: iconsCache.iconSize.x,
        sHeight: iconsCache.iconSize.y,
        dWidth: sourceIconData.size.x,
        dHeight: sourceIconData.size.y,
      };
      iconsCache.items.set(cacheKey, iconCacheCoordinates);
    },

    clearCanvas(context) {
      // Use the identity matrix while clearing the canvas
      context.setTransform(1, 0, 0, 1, 0, 0);
      context.clearRect(0, 0, context.canvas.width, context.canvas.height);

      // Restore the transform
      context.restore();
    },

    latLngToCanvasCoords(latLng) {
      if(!this.mapInstance) {
        return null;
      }

      // coordinates from top left corner of the map
      const layerPoint = this.mapInstance.latLngToLayerPoint(latLng);

      // coordinates from top left corner of the canvas tile
      const canvasCoords = layerPoint.subtract(this.getTileCoordinates());

      if(this.isCoordinatesFitCanvas(canvasCoords)) {
        return canvasCoords;
      } else {
        return null;
      }
    },

    isCoordinatesFitCanvas(coordinates) {
      if(coordinates.x < -20 || coordinates.y < -20) {
        return false;
      }

      return (
        coordinates.x < this.canvasSize.x + 20 &&
        coordinates.y < this.canvasSize.y + 20
      );
    },

    isVesselInTile(vesselIMO) {
      const vessel = this.cachedVessels.get(vesselIMO);
      if(vessel) {
        const coordinates = apiCoordsToLatLng(getNested(vessel, 'geometry.coordinates', [0, 0]));
        return this.tileLatLngBounds.contains(coordinates);
      }
    },

    getMapZoom() {
      return this.mapInstance.getZoom();
    },

    getPixelOrigin() {
      // console.log('pixel origin', JSON.stringify(this.mapInstance.getPixelOrigin()));
      return this.mapInstance.getPixelOrigin();
    },

    getTileCoordinates() {
      // relative tile coordinates to map top left corner
      // note: when map zoom animate is performing
      // an old pixelOrigin is used, so it's impossible to display objects until a zoom event ends
      const point = this.coords.scaleBy(this.canvasSize);
      return point.subtract(this.getPixelOrigin());
    },

    getLatLngByPixelCoordinates(point) {
      return this.mapInstance.unproject(point, this.getMapZoom());
    },
  },

  computed: {
    mapInstance() {
      return getNested(this.$store, 'state.map.mapInstance', null);
    },

    vesselsFilterParameters() {
      return getNested(this.$store, 'state.map.vesselsFilterParameters', {});
    },

    cachedVessels() {
      return getNested(this.$store, 'state.map.vessels', new Map());
    },

    cachedVesselsLoaded() {
      return getNested(this.$store, 'state.map.vesselsLoaded', false);
    },

    zoomInProgress() {
      return getNested(this.$store, 'state.map.zoomInProgress', null);
    },

    vessels() {
      return getNested(this.$store, 'state.map.vessels', null);
    },

    highlightedVesselIMO() {
      return getNested(this.$store, 'state.map.highlightedVesselIMO', null);
    },

    markersSize() {
      return getNested(this.$store, 'state.map.markers', {}).size;
    },

    tileLatLngBounds() {
      const padding = 14;
      let topLeftPoint = this.coords.scaleBy(this.canvasSize);

      // we need to subtract paddings to display overlapping vessels
      topLeftPoint = topLeftPoint.subtract([padding, padding]);

      let bottomRightPoint = this.coords.scaleBy(this.canvasSize);
      bottomRightPoint = bottomRightPoint.add([  // we need to add paddings to display overlapping vessels
        this.canvasSize.x + padding,
        this.canvasSize.y + padding,
      ]);

      return L.latLngBounds([
        this.getLatLngByPixelCoordinates(topLeftPoint),
        this.getLatLngByPixelCoordinates(bottomRightPoint),
      ]);
    },

    canvasStyle() {
      return {
        // 'border': `1px solid rgba(150, 150, 150, 0.2)`,
      };
    },
  },

  watch: {
    async vesselsFilterParameters() {
      await this.drawVessels();
    },

    async highlightedVesselIMO(currentIMO, previousIMO) {
      if(currentIMO && this.isVesselInTile(currentIMO)) {
        await this.drawVesselIcon(this.cachedVessels.get(currentIMO));
      }
      if(previousIMO && this.isVesselInTile(previousIMO)) {
        await this.drawVesselIcon(this.cachedVessels.get(previousIMO));
      }
    },

    async zoomInProgress(value) {
      if(!value) {
        await this.drawVessels();
      }
    },
  },
}


function getIconsCacheObject(options) {
  const cellSize = {x: 25, y: 25};
  const arrowIconSize = {x: 22, y: 22};
  const circleIconSize = {x: 11, y: 11};
  const cols = 10;
  const rows = 20;

  let cacheObject = {
    iconSize: cellSize,
    cols: cols,
    rows: rows,
    context: createContext(
      'icons-cache-canvas',
      cols * cellSize.x,
      rows * cellSize.y
    ),
    icons: {
      // all vessel icons
      // note: icon sizes could be adjusted here
      'arrow-light': {icon: null, size: arrowIconSize},
      'arrow-dark': {icon: null, size: arrowIconSize},
      'arrow-highlight': {icon: null, size: arrowIconSize},
      'arrow-error': {icon: null, size: arrowIconSize},
      'circle-light': {icon: null, size: circleIconSize},
      'circle-dark': {icon: null, size: circleIconSize},
      'circle-highlight': {icon: null, size: circleIconSize},
      'circle-error': {icon: null, size: circleIconSize},
    },
    items: new Map([
      // [<key>, <icon-coordinates>]
    ]),
  }

  for(let imageName of Object.keys(cacheObject.icons)) {
    getIconImage(
      require(`@/assets/icons/vessels/${imageName}.svg`)
    ).then(image => {
      cacheObject.icons[imageName].icon = image
    });
  }

  if(options && options.debug) {
    const divElement = document.createElement('div');
    divElement.classList.add('debug-canvas-cache');
    divElement.appendChild(cacheObject.context.canvas);
    document.body.appendChild(divElement);
    console.log('cache canvas', divElement);
  }

  // todo: return promise to wait
  return cacheObject;
}

async function getIconImage(sourceImage) {
  const image = new Image();
  image.src = sourceImage;

  return new Promise((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = () => reject(image);
  });
}

function createContext(id, width=300, height=300) {
  let canvasElement = document.createElement('canvas');
  canvasElement.id = id;
  canvasElement.width = width;
  canvasElement.height = height;
  return canvasElement.getContext('2d');
}
</script>

<style>
.debug-canvas-cache {
  position: fixed;
  bottom: 120px;
  right: 40px;
  z-index: 9000;
  background-color: rgba(255, 158, 22, 0.2);
}
</style>